import { useRef, useState, useCallback, useEffect } from "react";
import Bugsnag from "@bugsnag/js";
import { parseFile } from "../../../services/pepWatchUtils";
import { validateParsedData } from "./DataValidation";

// Enhanced state management for better debugging and UX
interface ValidationState {
  isActive: boolean;
  progress: { current: number; total: number } | null;
  sessionId: string; // Unique identifier for each validation session
  startTime: number | null;
  estimatedTimeRemaining: number | null;
}

// Generate unique session ID for each validation
const generateSessionId = () => `validation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Calculate estimated time remaining based on processing speed
const calculateETA = (current: number, total: number, startTime: number): number | null => {
  if (current === 0 || startTime === 0) return null;

  const elapsed = Date.now() - startTime;
  const rate = current / elapsed; // items per millisecond
  const remaining = total - current;

  return remaining / rate;
};

export const useFileUpload = () => {
  // Core file state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Validation results
  const [validData, setValidData] = useState<any[]>([]);
  const [invalidData, setInvalidData] = useState<any[]>([]);

  // Enhanced validation state
  const [validationState, setValidationState] = useState<ValidationState>({
    isActive: false,
    progress: null,
    sessionId: "",
    startTime: null,
    estimatedTimeRemaining: null,
  });

  // Legacy compatibility - expose individual validation properties
  const validationProgress = validationState.progress;
  const isValidating = validationState.isActive;

  // Refs for managing async operations and cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const progressUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Enhanced progress tracking with rate limiting and ETA calculation
  const handleProgressUpdate = useCallback((current: number, total: number) => {
    // Debounce progress updates to prevent excessive re-renders
    if (progressUpdateTimeoutRef.current) {
      clearTimeout(progressUpdateTimeoutRef.current);
    }

    progressUpdateTimeoutRef.current = setTimeout(() => {
      setValidationState((prev) => {
        // Only update if validation is still active and session hasn't changed
        if (!prev.isActive) return prev;

        const estimatedTimeRemaining = prev.startTime ? calculateETA(current, total, prev.startTime) : null;

        return {
          ...prev,
          progress: { current, total },
          estimatedTimeRemaining,
        };
      });
    }, 50); // 20fps update rate
  }, []);

  // Enhanced file upload with better error handling and state management
  const handleFileUpload = useCallback(
    async (file: File) => {
      // Cancel any existing validation
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller and session
      abortControllerRef.current = new AbortController();
      const sessionId = generateSessionId();

      // Reset all state
      setUploadedFile(file);
      setIsLoading(true);
      setParseError(null);
      setValidData([]);
      setInvalidData([]);
      setParsedData([]);

      // Initialize validation state
      setValidationState({
        isActive: false,
        progress: null,
        sessionId,
        startTime: null,
        estimatedTimeRemaining: null,
      });

      try {
        const rawData = await parseFile(file);

        // Check if operation was aborted during parsing
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        if (rawData.length === 0) {
          setParseError("Engin gögn fundust í skjalinu. Vinsamlegt farið eftir sniðmáti.");
          return;
        }

        setParsedData(rawData);

        // Start validation phase
        setValidationState((prev) => ({
          ...prev,
          isActive: true,
          startTime: Date.now(),
          progress: { current: 0, total: rawData.length },
        }));

        // Run validation with progress tracking
        const validated = await validateParsedData(rawData, handleProgressUpdate, abortControllerRef.current.signal);

        // Check if validation was aborted
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        // Update results
        setValidData(validated.validRows);
        setInvalidData(validated.invalidRows);

        // Mark validation as complete
        setValidationState((prev) => ({
          ...prev,
          isActive: false,
          progress: { current: rawData.length, total: rawData.length },
          estimatedTimeRemaining: null,
        }));
      } catch (error) {
        // Handle different types of errors
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        if (error instanceof Error && error.message.includes("aborted")) {
          return;
        }

        // Log unexpected errors

        if (error instanceof Error) {
          Bugsnag.notify(error, (event) => {
            event.addMetadata("fileUpload", {
              fileName: file.name,
              fileSize: file.size,
              sessionId: sessionId,
            });
          });
        }

        const errorMessage = error instanceof Error ? error.message : "Óþekkt villa kom upp.";

        setParseError(errorMessage);

        // Reset validation state on error
        setValidationState((prev) => ({
          ...prev,
          isActive: false,
          progress: null,
          estimatedTimeRemaining: null,
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [handleProgressUpdate]
  );

  // Enhanced reset with comprehensive cleanup
  const resetFileUpload = useCallback(() => {
    // Cancel any ongoing operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear any pending timeouts
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    if (progressUpdateTimeoutRef.current) {
      clearTimeout(progressUpdateTimeoutRef.current);
      progressUpdateTimeoutRef.current = null;
    }

    // Reset all state
    setUploadedFile(null);
    setParsedData([]);
    setValidData([]);
    setInvalidData([]);
    setParseError(null);
    setIsLoading(false);

    setValidationState({
      isActive: false,
      progress: null,
      sessionId: "",
      startTime: null,
      estimatedTimeRemaining: null,
    });
  }, []);

  // Enhanced stop validation with proper cleanup
  const stopValidation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();

      // Update state to reflect stopped validation
      setValidationState((prev) => ({
        ...prev,
        isActive: false,
        progress: null,
        estimatedTimeRemaining: null,
      }));

      // Clear the file and reset state after a short delay to allow for cleanup
      cleanupTimeoutRef.current = setTimeout(() => {
        resetFileUpload();
      }, 100);
    }
  }, [resetFileUpload]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Clear timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }

      if (progressUpdateTimeoutRef.current) {
        clearTimeout(progressUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced validation state getters for better debugging
  const validationStats = {
    sessionId: validationState.sessionId,
    isActive: validationState.isActive,
    progress: validationState.progress,
    estimatedTimeRemaining: validationState.estimatedTimeRemaining,
    startTime: validationState.startTime,
    // Calculate processing rate if available
    processingRate:
      validationState.progress && validationState.startTime
        ? (validationState.progress.current / (Date.now() - validationState.startTime)) * 1000 // items per second
        : null,
  };

  return {
    // Core file state
    uploadedFile,
    parsedData,
    parseError,
    isLoading,

    // Validation results
    validData,
    invalidData,

    // Legacy compatibility for progress tracking
    validationProgress,
    isValidating,

    // Enhanced validation state
    validationStats,

    // Actions
    handleFileUpload,
    resetFileUpload,
    stopValidation,
  };
};
